import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { defaultNotifier } from 'functions/notificationHandler';
import {
  VuiPopUp,
  VuiFormGroup,
} from '@topkamera/viso-ui';
import { EnumType } from 'json-to-graphql-query';
import {
  createSideBarCustomSection,
  deleteSideBarCustomSection,
  deleteSideBarCustomShortcut,
  updateSideBarCustomSection,
} from 'api/workspace';
import './SidebarPopUp.scss';

const runMutation = async ({ mutationFn, args, failureMessage }) => {
  try {
    const response = await mutationFn(args)

    if (response?.data?.errors) {
      const error = response.data.errors
      defaultNotifier(failureMessage || error)
      return { ok: false, error }
    }

    return { ok: true, data: response?.data?.data }
  } catch (error) {
    defaultNotifier(failureMessage || error)
    return { ok: false, error }
  }
}

export const SidebarPopUp = ({
  isVisible = false,
  title,
  description,
  mutationParams,
  onClose,
  onSuccess,
  children,
  confirmDisabled,
}) => {

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const handleConfirm = useCallback(async () => {
    const resolvedArgs =
      mutationParams && typeof mutationParams.args === 'function'
        ? mutationParams.args()
        : (mutationParams && mutationParams.args)

    const result = await runMutation({
      mutationFn: mutationParams.fn,
      args: resolvedArgs,
      failureMessage: mutationParams.failure,
    })

    if (!result.ok) {
      return
    }

    if (onSuccess) {
      onSuccess(result.data)
    }

    handleClose()
  }, [handleClose, mutationParams, onSuccess, title])

  return (
    <VuiPopUp
      isVisible={isVisible}
      title={title}
      description={description}
      confirmBtn={{
        color: 'success',
        label: 'Confirm',
        style: 'color',
        onClick: handleConfirm,
        disabled: confirmDisabled,
      }}
      cancelBtn={{
        color: 'accent1',
        label: 'Cancel',
        onClick: handleClose,
      }}
      closeOnConfirm={false}
      onClose={handleClose}
    >
      {children &&
        <div className='viso-sidebar-popup-content'>
          {children}
        </div>
      }
    </VuiPopUp>
  )
}

export const SectionPopUp = ({
  isVisible = false,
  mutationArgs,
  onClose,
  onSuccess,
  isUpdate
}) => {
  const [sectionName, setSectionName] = useState('')
  const sectionNameRef = useRef('')
  const wasVisibleRef = useRef(false)

  useEffect(() => {
    const wasVisible = wasVisibleRef.current
    wasVisibleRef.current = isVisible

    if (!isVisible || wasVisible) {
      return
    }

    const nextName =
      (mutationArgs && typeof mutationArgs.name === 'string' ? mutationArgs.name : '')
    sectionNameRef.current = nextName
    setSectionName(nextName)
  }, [isVisible])

  const getResolvedArgs = () => {
    return {
      ...(mutationArgs || {}),
      ...(!isUpdate ? { name: sectionNameRef.current } : {}),
      ...(isUpdate && sectionNameRef.current ? { name: sectionNameRef.current } : {}),
    }
  }

  return (
    <SidebarPopUp
      title={isUpdate ? "Update sidebar section" : "Create a sidebar section"}
      description={!isUpdate && "Give your section a name your visitors will recognize."}
      mutationParams={{
        failure: isUpdate ? "Couldn't update the section." : "Couldn't create the section.",
        fn: isUpdate ? updateSideBarCustomSection : createSideBarCustomSection,
        args: getResolvedArgs,
      }}
      isVisible={isVisible}
      onClose={onClose}
      onSuccess={onSuccess}
      confirmDisabled={!sectionName}
    >
      <div className={classNames('vui-flex flex-grow-1', !isUpdate && 'vui-py-5')}>
        <VuiFormGroup
          fieldProps={{
            onChange: (e) => {
              const nextValue = e?.target?.value ?? e ?? ''
              sectionNameRef.current = nextValue
              setSectionName(nextValue)
            },
            placeholder: 'Label name',
            value: sectionName
          }}
          groupLayout="block"
          fieldType="textinput"
          id="labelName"
        />
      </div>
    </SidebarPopUp>
  )
}

export const ShortcutPopUp = ({
  isVisible = false,
  CreateShortcutPopUpProps,
  UpdateShortcutPopUpProps,
  linkOptions,
  mutationArgs,
  onClose,
  onSuccess,
  isUpdate,
}) => {
  const [linkedPage, setLinkedPage] = useState([])
  const [shortcutName, setShortcutName] = useState('')
  const [shortcutIcon, setShortcutIcon] = useState('profile')
  const [shortcutType, setShortcutType] = useState(null)
  const [isSelectIconVisible, setIsSelectIconVisible] = useState(false)
  const linkedPageRef = useRef([])
  const shortcutNameRef = useRef('')
  const shortcutIconRef = useRef('profile')
  const shortcutTypeRef = useRef(null)
  const lastInitKeyRef = useRef(null)

  const resolveTypeFromUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      return null
    }

    if (url.includes('/dashboards')) {
      return 'dashboards'
    }

    if (url.includes('/incident')) {
      return 'incidents'
    }

    if (url.includes('/reports')) {
      return 'reports'
    }

    return null
  }, [])

  const getOptionLabel = useCallback((option) => {
    if (!option || typeof option !== 'object') {
      return ''
    }

    const label = option.label ?? option.name
    return typeof label === 'string' ? label : ''
  }, [])

  const resolveSelectValue = useCallback(
    (rawValue) => {
      const options = (linkOptions && linkOptions.length ? linkOptions : [])

      const rawAsArray = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : [])
      const first = rawAsArray.length ? rawAsArray[0] : null

      if (!first) {
        return []
      }

      if (typeof first === 'object' && first.value) {
        return [first]
      }

      if (typeof first === 'string') {
        const match = options.find((o) => o && o.value === first)
        if (match) {
          return [match]
        }

        return first ? [{ label: first, value: first }] : []
      }

      return []
    },
    [linkOptions],
  )

  useEffect(() => {
    if (!isVisible) {
      lastInitKeyRef.current = null
      return
    }

    const initKey = `${isUpdate ? 'update' : 'create'}:${mutationArgs && mutationArgs.id ? mutationArgs.id : ''}`
    if (lastInitKeyRef.current === initKey) {
      return
    }

    lastInitKeyRef.current = initKey

    const nextLinkedPage = resolveSelectValue(mutationArgs && mutationArgs.url)
    setLinkedPage(nextLinkedPage)
    linkedPageRef.current = nextLinkedPage

    const resolvedLinked = resolveSelectValue(nextLinkedPage)
    const selectedOption = resolvedLinked.length ? resolvedLinked[0] : null
    const selectedLabel = getOptionLabel(selectedOption)
    const existingName = (mutationArgs && typeof mutationArgs.name === 'string' ? mutationArgs.name : '')
    const nextName = isUpdate && existingName && selectedLabel && existingName === selectedLabel ? '' : existingName

    setShortcutName(nextName)
    shortcutNameRef.current = nextName

    const nextIcon = (mutationArgs && typeof mutationArgs.icon === 'string' && mutationArgs.icon) ? mutationArgs.icon : 'profile'
    setShortcutIcon(nextIcon)
    shortcutIconRef.current = nextIcon

    const selectedOptionType = nextLinkedPage && nextLinkedPage.length ? nextLinkedPage[0]?.category : null
    const nextType = (mutationArgs && typeof mutationArgs.type === 'string' && mutationArgs.type)
      ? mutationArgs.type
      : (selectedOptionType || resolveTypeFromUrl(mutationArgs && mutationArgs.url))
    setShortcutType(nextType)
    shortcutTypeRef.current = nextType
  }, [getOptionLabel, isUpdate, isVisible, mutationArgs, resolveSelectValue, resolveTypeFromUrl])

  const getResolvedArgs = () => {
    const resolvedSelect = resolveSelectValue(linkedPageRef.current)
    const selectedOption = resolvedSelect.length ? resolvedSelect[0] : null

    return {
      ...(mutationArgs || {}),
      name: shortcutNameRef.current ? shortcutNameRef.current : (selectedOption?.label || ''),
      ...(selectedOption && selectedOption.value ? { url: selectedOption.value } : {}),
      ...(shortcutIconRef.current ? { icon: shortcutIconRef.current } : {}),
      ...(shortcutTypeRef.current ? { type: new EnumType(shortcutTypeRef.current) } : {}),
    }
  }

  const canUseIconInput = typeof VuiIconInput === 'function'

  return (
    <SidebarPopUp
      title={isUpdate ? "Update shortcut" : "Add shortcut to section"}
      description={!isUpdate && "Select an Item to add a shortcut, and set a custom title."}
      mutationParams={{
        failure: isUpdate ? "Couldn't update the shortcut." : "Couldn't create the shortcut.",
        fn: isUpdate ? updateSideBarCustomShortcut : createSideBarCustomShortcut,
        args: getResolvedArgs,
      }}
      isVisible={isVisible}
      onClose={onClose}
      popUpProps={isUpdate ? UpdateShortcutPopUpProps : CreateShortcutPopUpProps}
      onSuccess={onSuccess}
      confirmDisabled={linkedPage.length === 0 || !shortcutType}
    >
      <div className={classNames('vui-flex vui-flex-grow', !isUpdate && 'vui-pt-5')}>
        <VuiFormGroup
          fieldProps={{
            elementId: 'popoverRoot',
            scrollHeight: 6,
            isBadge: true,
            hasSearch: true,
            onChange: (value) => {
              const resolved = resolveSelectValue(value)
              linkedPageRef.current = resolved
              setLinkedPage(resolved)

              const selectedOption = resolved && resolved.length ? resolved[0] : null
              const derivedType = (selectedOption && selectedOption.category)
                ? selectedOption.category
                : resolveTypeFromUrl(selectedOption && selectedOption.value)

              shortcutTypeRef.current = derivedType
              setShortcutType(derivedType)
            },
            placeholder: 'Linked page',
            value: linkedPage || [],
            options: (linkOptions && linkOptions.length ? linkOptions : []),
            type: 'group',
            typeProps: {
              categories: [
                {
                  label: 'Dashboards',
                  value: 'dashboards',
                },
                {
                  label: 'Incident Boards',
                  value: 'incidents',
                },
                {
                  label: 'Reports',
                  value: 'reports',
                },
              ],
              fixedHeight: 200,
            },
          }}
          fieldType="selectinput"
          id="linkedPage"
        />
      </div>
      <div className='vui-flex vui-gap-8'>
        {canUseIconInput ? (
          <VuiIconInput
            icon={shortcutIcon}
            size="sm"
            onClick={() => setIsSelectIconVisible(true)}
          />
        ) : (
          <VuiButton
            color="accent0"
            iconLeft={shortcutIcon}
            onClick={() => setIsSelectIconVisible(true)}
            iconSize='sm'
            radius="rounded"
            style="outline"
          />
        )}
        <VuiFormGroup
          fieldProps={{
            onChange: (e) => {
              const nextValue = e?.target?.value ?? e ?? ''
              shortcutNameRef.current = nextValue
              setShortcutName(nextValue)
            },
            placeholder: linkedPage?.[0]?.label || 'Display text',
            value: shortcutName
          }}
          groupLayout="block"
          fieldType="textinput"
          id="shortcutLabel"
        />
      </div>
      <SelectIconPopUp
        isVisible={isSelectIconVisible}
        initialValue={shortcutIcon}
        onClose={() => setIsSelectIconVisible(false)}
        onApply={(icon) => {
          const resolvedIcon = typeof icon === 'string' && icon ? icon : 'profile'
          shortcutIconRef.current = resolvedIcon
          setShortcutIcon(resolvedIcon)
        }}
      />
    </SidebarPopUp>
  )
}

export const DeletePopUp = ({
  isVisible = false,
  DeleteSectionPopUpProps,
  DeleteShortcutPopUpProps,
  mutationArgs,
  onClose,
  onSuccess,
  isShortcut,
}) => {
  return (
    <SidebarPopUp
      title={isShortcut ? "Confirm to remove shortcut" : "Confirm to remove section"}
      description={isShortcut ? "Removing the shortcut will not delete the linked item." : "Removing the section will not lead to data loss."}
      mutationParams={{
        failure: isShortcut ? "Couldn't delete the shortcut." : "Couldn't delete the section.",
        fn: isShortcut ? deleteSideBarCustomShortcut : deleteSideBarCustomSection,
        args: mutationArgs,
      }}
      isVisible={isVisible}
      onClose={onClose}
      popUpProps={isShortcut ? DeleteShortcutPopUpProps : DeleteSectionPopUpProps}
      onSuccess={onSuccess}
    />
  )
}